import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useOsmPois } from '@/api/hooks/useOsmPois';
import type { UserPoiCollection } from '@/types/osm';

const OSM_CATS = [
  ['fuel', 'Fuel stations'],
  ['food', 'Restaurants & cafes'],
  ['lodging', 'Hotels'],
  ['shop', 'Shops'],
  ['health', 'Hospitals & pharmacies'],
  ['transit', 'Transit'],
  ['parking', 'Parking'],
  ['leisure', 'Parks & leisure'],
  ['civic', 'Civic'],
  ['religion', 'Worship'],
  ['tourism', 'Tourism'],
  ['entertainment', 'Entertainment'],
  ['info', 'ATM & info'],
  ['toll_booth', 'Toll plazas'],
  ['lift_gate', 'Boom gates'],
  ['speed_camera', 'Speed cameras'],
  ['traffic_signals', 'Robots (traffic lights)'],
  ['stops_giveway', 'Stops & yields'],
  ['street_lamp', 'Street lamps'],
] as const;

interface PoiLayerPanelProps {
  enabledCategories: Set<string>;
  setEnabledCategories: (s: Set<string>) => void;
  showOsmPois: boolean;
  setShowOsmPois: (b: boolean) => void;
  showUserPois: boolean;
  setShowUserPois: (b: boolean) => void;
  showMerchants: boolean;
  setShowMerchants: (b: boolean) => void;
  userPois: UserPoiCollection | null;
  setUserPois: (p: UserPoiCollection | null) => void;
}

export default function PoiLayerPanel({
  enabledCategories,
  setEnabledCategories,
  showOsmPois,
  setShowOsmPois,
  showUserPois,
  setShowUserPois,
  showMerchants,
  setShowMerchants,
  userPois,
  setUserPois,
}: PoiLayerPanelProps) {
  const { data, isLoading, error } = useOsmPois({ source: 'mariadb' });

  useEffect(() => {
    if (data && !userPois) setUserPois(data);
  }, [data, userPois, setUserPois]);

  function toggleCat(k: string) {
    const next = new Set(enabledCategories);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setEnabledCategories(next);
  }

  function allOn() {
    setEnabledCategories(new Set(OSM_CATS.map(([k]) => k)));
  }
  function allOff() {
    setEnabledCategories(new Set());
  }
  function fuelOnly() {
    setEnabledCategories(new Set(['fuel']));
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div>
        <h3 className="text-sm font-semibold">Points of interest</h3>
        <p className="text-xs text-muted-foreground">Toggle categories rendered from OSM tiles.</p>
      </div>

      <Card>
        <CardContent className="space-y-1.5 p-3 text-xs">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Layer visibility
          </div>
          <label className="flex items-center justify-between">
            <span className="font-medium">Fleet merchants</span>
            <input
              type="checkbox"
              checked={showMerchants}
              onChange={(e) => setShowMerchants(e.target.checked)}
            />
          </label>
          <label className="flex items-center justify-between">
            <span className="font-medium">User POIs</span>
            <input
              type="checkbox"
              checked={showUserPois}
              onChange={(e) => setShowUserPois(e.target.checked)}
            />
          </label>
          <label className="flex items-center justify-between">
            <span className="font-medium">OSM POIs</span>
            <input
              type="checkbox"
              checked={showOsmPois}
              onChange={(e) => setShowOsmPois(e.target.checked)}
            />
          </label>
        </CardContent>
      </Card>

      <button
        type="button"
        onClick={fuelOnly}
        className="rounded border border-input bg-background px-2 py-1 text-xs text-primary hover:bg-accent"
      >
        Reset to fuel only
      </button>

      <Card>
        <CardContent className="space-y-1 p-3 text-xs text-muted-foreground">
          <div className="font-medium text-foreground">User POIs (MariaDB)</div>
          {isLoading && (
            <span className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </span>
          )}
          {error && <span className="text-red-700">{(error as Error).message}</span>}
          {data && <span>{data.features.length.toLocaleString('en-ZA')} POIs cached</span>}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-2 p-3 text-xs">
          <div className="font-medium">OSM basemap POI categories</div>
          <div className="flex gap-2 text-[11px]">
            <button type="button" className="text-primary hover:underline" onClick={allOn}>
              All on
            </button>
            <button type="button" className="text-muted-foreground hover:underline" onClick={allOff}>
              All off
            </button>
          </div>
          <div className="grid grid-cols-1 gap-1 pt-1">
            {OSM_CATS.map(([k, label]) => (
              <label key={k} className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={enabledCategories.has(k)}
                  onChange={() => toggleCat(k)}
                  disabled={!showOsmPois}
                />
                <span className={showOsmPois ? '' : 'text-muted-foreground'}>{label}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
